import { useContext, useEffect, useState } from 'react'
import type { MouseEvent } from 'react'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import { MODALS_ID, PAGES } from '../../constants'
import type { components, operations } from '../../constants/schema'

import { toast, HttpReq, updateToken } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'
import { ModalContext } from '../../provider/modalProvider'

import { useNavigate } from '../../hooks/useNavigate'

import { AlignCenterItem } from '../../components/common'
import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { Button } from '../../components/button'
import { Footer } from '../../components/footer'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { TermsBox } from '../../components/termsBox'
import { Text } from '../../components/text'
import { NoticeContainer } from '../../components/noticeContainer/NoticeContainer'
import { TermsDetailModal } from '../../components/modals/TermsDetailModal'
import { TermCheckbox } from './components/TermsCheckbox'
import SuccessSignUpModal from './modals/SuccessSignUpModal'

import MarketingImage from '../../assets/terms/signup/marketing.png'
import { Color } from '../../theme/color'

type PageName = operations['TermsController_findByPageName']['parameters']['path']['pageName']
type TermsDto = components['schemas']['TermsDto']
type Term = TermsDto & { approved: boolean }

const getTerms = async (pageName: PageName) => {
  return await HttpReq<TermsDto[]>({
    url: `/terms/page/${pageName}`,
    method: 'GET',
  })
}

export const SignUp = observer(() => {
  const { authStore, commonStore } = useContext(StoresContext)
  const { openModal, closeModal } = useContext(ModalContext)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  // SIGNUP 약관 목록
  const [terms, setTerms] = useState<Term[] | null>(null)
  // MARKETING 약관 목록
  const [marketingTerms, setMarketingTerms] = useState<{
    email: Term | null
    sms: Term | null
  }>({ email: null, sms: null })
  // 전체 약관 동의 checked 여부
  const [isAllAgreed, setIsAllAgreed] = useState<boolean>(false)
  // 필수 약관 동의 여부(회원가입 가능 여부)
  const [isAgreed, setIsAgreed] = useState<boolean>(false)
  // 약관 상세 보기 필요한 약관 id
  const [termsId, setTermsId] = useState<number | undefined>()

  const navigate = useNavigate()

  useEffect(() => {
    commonStore.setShowTab(false)
    authStore.getUserInfo()
    initTerms()
  }, [])

  useEffect(() => {
    if (!terms || !marketingTerms.email || !marketingTerms.sms) return

    const {
      email: { approved: emailApproved },
      sms: { approved: smsApproved },
    } = marketingTerms
    const isMarketingApproved = emailApproved && smsApproved

    setIsAllAgreed(terms.every((v) => v.approved) && isMarketingApproved)
    setIsAgreed(terms.every((v) => v.approved || !v.required))
  }, [terms, marketingTerms])

  const openTermsModal = () => {
    openModal({
      id: MODALS_ID.TERMS_DETAIL,
      Component: () => (
        <TermsDetailModal
          termsId={termsId}
          buttonProps={{ footerContent: '닫기', type: 'primary' }}
          onDismiss={() => closeModal(MODALS_ID.TERMS_DETAIL)}
        />
      ),
    })
  }

  const openSuccessSignUpModal = () => {
    openModal({
      id: MODALS_ID.SUCCESS_SIGNUP,
      Component: () => (
        <SuccessSignUpModal
          onDismiss={() => {
            closeModal(MODALS_ID.SUCCESS_SIGNUP)
            navigate('/home')
          }}
        />
      ),
    })
  }

  const initTerms = async () => {
    setIsLoading(true)
    const signupResponse = await getTerms(PAGES.SIGN_UP as PageName)
    const emailResponse = await getTerms(PAGES.SIGNUP_MKT_EMAIL as PageName)
    const smsResponse = await getTerms(PAGES.SIGNUP_MKT_SMS as PageName)

    setTerms(signupResponse.map((v) => ({ ...v, approved: false })))
    setMarketingTerms({ email: { ...emailResponse[0], approved: false }, sms: { ...smsResponse[0], approved: false } })
    setIsLoading(false)
  }

  const toggleAllTerms = (checked: boolean) => {
    setTerms((prevState) => {
      if (!prevState) return prevState
      return prevState.map((v) => ({ ...v, approved: checked }))
    })
    setMarketingTerms(({ email, sms }) => {
      if (!email || !sms) return { email, sms }
      return { email: { ...email, approved: checked }, sms: { ...sms, approved: checked } }
    })
  }

  const toggleMarketingApproved = () => {
    if (!marketingTerms.email || !marketingTerms.sms) return

    const {
      email: { approved: emailApproved },
      sms: { approved: smsApproved },
    } = marketingTerms
    const isApproved = emailApproved && smsApproved

    setMarketingTerms(({ email, sms }) => {
      if (!email || !sms) return { email, sms }
      return {
        email: { ...email, approved: !isApproved },
        sms: { ...sms, approved: !isApproved },
      }
    })
  }

  const toggleEmailApproved = () => {
    setMarketingTerms((prevState) => {
      if (!prevState.email || !prevState.sms) return prevState
      const { email } = prevState
      return { ...prevState, email: { ...email, approved: !email.approved } }
    })
  }

  const toggleSmsApproved = () => {
    setMarketingTerms((prevState) => {
      if (!prevState.email || !prevState.sms) return prevState
      const { sms } = prevState
      return { ...prevState, sms: { ...sms, approved: !sms.approved } }
    })
  }

  const signUp = async () => {
    if (!terms || !marketingTerms.email || !marketingTerms.sms) return
    try {
      const requestSignupTerms: components['schemas']['UserTermsApproveDto'][] = []

      terms.forEach(({ id, approved }) => {
        requestSignupTerms.push({ approved, termsId: id })
      })

      const { email, sms } = marketingTerms
      requestSignupTerms.push({ termsId: email.id, approved: email.approved })
      requestSignupTerms.push({ termsId: sms.id, approved: sms.approved })

      await authStore.signUp({ terms: requestSignupTerms })
      await authStore.getUserInfo()
      updateToken('save')

      openSuccessSignUpModal()
    } catch (err) {
      toast('약관동의에 실패하였습니다.', err)
    }
  }

  return (
    <Wrap>
      <Header
        type="SUB"
        title="약관동의"
        isRoute={false}
        onBack={async () => {
          await authStore.signOut()
          navigate('/signIn')
        }}
      />
      <StyledContent>
        <Text size={24} weight={600} lineHeight={34}>
          아래 약관에 동의 후<br />
          서비스 이용이 가능해요.
        </Text>
        {isLoading ? (
          <LoadingWrap>
            <Spinner />
          </LoadingWrap>
        ) : (
          <>
            <TermsBox title="전체 약관 동의" isAgreed={isAllAgreed} onChange={toggleAllTerms} isArrow={false} />
            {terms && marketingTerms.email && marketingTerms.sms ? (
              <ul>
                {terms
                  // TODO: 임시) 회원가입 이용약관 순서 14세 이상 > 서비스 이용약관 > 개인정보 수집 순 정렬
                  .sort((a, b) => b.id - a.id)
                  .sort(({ required }) => (required ? -1 : 1))
                  .map(({ id, name, required, contents, approved }) => {
                    const toggle = () => {
                      setTerms((prevState) => {
                        if (!prevState) return prevState
                        return prevState.map((v) => (v.id === id ? { ...v, approved: !v.approved } : v))
                      })
                    }

                    const showDetail = (e: MouseEvent<HTMLSpanElement>) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setTermsId(id)
                      openTermsModal()
                    }

                    const termCheckboxProps = { approved, name, required, contents, toggle, showDetail }

                    return <TermCheckbox key={id} {...termCheckboxProps} />
                  })}
                <TermCheckbox
                  approved={Boolean(marketingTerms.email?.approved || marketingTerms.sms?.approved)}
                  name="마케팅 목적의 개인정보 수집 및 이용 동의"
                  required={false}
                  toggle={toggleMarketingApproved}
                />
                <MarketingImageListItem>
                  <img src={MarketingImage} alt="마케팅 목적의 개인정보 수집 및 이용 동의" />
                </MarketingImageListItem>
                <TermCheckbox
                  approved={Boolean(marketingTerms.email?.approved || marketingTerms.sms?.approved)}
                  name="마케팅 정보 수신 동의"
                  required={false}
                  toggle={toggleMarketingApproved}
                />
                <MarketingRowHalfBox>
                  <TermCheckbox approved={marketingTerms.email?.approved} name="이메일" toggle={toggleEmailApproved} />
                  <TermCheckbox approved={marketingTerms.sms?.approved} name="문자(SMS)" toggle={toggleSmsApproved} />
                </MarketingRowHalfBox>
              </ul>
            ) : null}
          </>
        )}
        <NoticeBox>
          <li>
            <span className="list-dot">•</span>
            <Text size={12} color="text_secondary" block={true}>
              (선택) 사항 동의 거부 시에도 회원가입 및 아티피오 서비스 이용이 가능합니다.
            </Text>
          </li>
          <li>
            <span className="list-dot">•</span>
            <Text size={12} color="text_secondary" block={true}>
              (선택) 사항 동의 거부 시 회원가입 과정에서 수집된 휴대폰번호와 이메일은 본 목적(회원가입 및
              회원관리)으로만 활용됩니다.
            </Text>
          </li>
          <li>
            <span className="list-dot">•</span>
            <Text size={12} color="text_secondary" block={true}>
              본 마케팅 정보 수신 동의는 아티피오 앱 내 더보기&gt;알림 설정&gt;마케팅 정보 알림 설정을 통해 언제든지
              철회할 수 있습니다.
            </Text>
          </li>
        </NoticeBox>
      </StyledContent>
      <Footer>
        <Button type="primary" size="large" disabled={!isAgreed} block={true} onClick={signUp}>
          가입완료
        </Button>
      </Footer>
    </Wrap>
  )
})

const StyledContent = styled(Content)`
  padding: 64px 24px 0;
  > div {
    margin: 48px 0 16px;
  }
`

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: calc(100% - 158px - 100px);
`

const MarketingImageListItem = styled.li`
  list-style: none;
  > img {
    padding-left: 48px;
  }
`

const MarketingRowHalfBox = styled(AlignCenterItem)`
  gap: 40px;
  padding-left: 48px;
  > * {
    padding: 0;
  }
`

const NoticeBox = styled.ul`
  margin: 0;
  margin-top: 24px;
  > li {
    display: flex;
    gap: 2px;
    color: ${Color.text_secondary};
    list-style: none;
    > .list-dot {
      line-height: 12px;
    }
  }
`
